import { APIStatus } from "@/api/APIStatus.type";
import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import Pagination from "@/components/molecules/Pagination/Pagination";
import { SquarePlayer } from "@/components/molecules/Player/SquarePlayer/SquarePlayer";
import CardRow from "@/containers/CardRow";
import { checkMark } from "@/designSystemIcons";
import { arrow } from "@/designSystemIcons";
import { convertPlayerPrice } from "@/functions/convertPlayerPrice";
import { getPriceType } from "@/functions/getPriceType";
import { cn } from "@/lib/utils";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { SortDirection } from "@/redux/myClub/myPlayersSlice";
import { SortBy } from "@/redux/myClub/myPlayersSlice";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import { generateNavigationUrl } from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

export const PlayerTable = ({
  fetchPlayersStatus,
  players,
  page,
  setPage,
  totalAmountOfPages,
  sortBy,
  sortDirection,
  setSortBy,
  setSortDirection,
  isSelecting,
  selectedPlayers,
  handleSelectPlayer,
}: {
  fetchPlayersStatus: APIStatus;
  players: SearchedPlayer[];
  page: number;
  setPage: (page: number) => void;
  totalAmountOfPages: number;
  sortBy: SortBy;
  sortDirection: SortDirection;
  setSortBy: (sortBy: SortBy) => void;
  setSortDirection: (sortDirection: SortDirection) => void;
  isSelecting: boolean;
  selectedPlayers: SearchedPlayer[];
  handleSelectPlayer: (player: SearchedPlayer, isSelected: boolean) => void;
}) => {
  const versionAssets = useSelector(getVersionAssetsSelector);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const navigate = useNavigate();

  return (
    <main className="flex flex-col gap-y-2 w-full">
      {!players.length &&
      (fetchPlayersStatus === "fulfilled" ||
        fetchPlayersStatus === "rejected") ? (
        <div className="w-full">
          <div className="bg-panel-middleground h-13 text-dark-secondary rounded-t-lg mb-4" />
          <CardRow>
            <div className="m-1 w-full bg-gray-900 flex items-center justify-center">
              No players found. Make a fresh Club Import to see your players.
            </div>
          </CardRow>
        </div>
      ) : (
        <table className="min-w-full text-center text-xs mobile:text-sm w-full table-fixed">
          <thead className="bg-panel-middleground h-13 text-dark-secondary">
            <tr>
              <th className="w-[25%] mobile:w-[30%] px-1.5 py-2 mobile:px-2 touch-device:px-4 text-left first:rounded-tl-lg">
                Player
              </th>
              <th
                className={cn(
                  "w-[10%] px-1.5 py-2 mobile:px-2 touch-device:px-4 relative cursor-pointer hover:bg-white/10",
                  sortBy === "rating" && "bg-white/10",
                )}
                onClick={() => {
                  if (sortBy === "rating") {
                    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                  } else {
                    setSortDirection("desc");
                  }
                  setSortBy("rating");
                }}
              >
                <div className="flex flex-row items-center justify-center gap-x-0.5 mobile:gap-x-1">
                  <div>Rating</div>
                  <div
                    className={cn(
                      "w-2 h-2 shrink-0",
                      sortDirection === "asc" &&
                        sortBy === "rating" &&
                        "rotate-180",
                    )}
                  >
                    {arrow}
                  </div>
                </div>
              </th>
              <th className="w-[10%] px-1.5 py-2 mobile:px-2 touch-device:px-4 hidden mobile:table-cell">
                Position
              </th>
              <th className="w-[15%] px-1.5 py-2 mobile:px-2 touch-device:px-4 hidden touch-device:table-cell">
                Version
              </th>
              <th
                className={cn(
                  "w-[10%] px-1.5 py-2 mobile:px-2 touch-device:px-4 relative cursor-pointer hover:bg-white/10",
                  sortBy === "price" && "bg-white/10",
                )}
                onClick={() => {
                  if (sortBy === "price") {
                    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                  } else {
                    setSortDirection("desc");
                  }
                  setSortBy("price");
                }}
              >
                <div className="flex flex-row items-center justify-center gap-x-0.5 mobile:gap-x-1">
                  <span>Price</span>
                  <div
                    className={cn(
                      "w-2 h-2 shrink-0",
                      sortDirection === "asc" &&
                        sortBy === "price" &&
                        "rotate-180",
                    )}
                  >
                    {arrow}
                  </div>
                </div>
              </th>
              <th
                className={cn(
                  "w-[10%] px-1.5 py-2 mobile:px-2 touch-device:px-4 relative cursor-pointer hover:bg-white/10",
                  sortBy === "profit" && "bg-white/10",
                )}
                onClick={() => {
                  if (sortBy === "profit") {
                    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                  } else {
                    setSortDirection("desc");
                  }
                  setSortBy("profit");
                }}
              >
                <div className="flex flex-row items-center justify-center gap-x-0.5 mobile:gap-x-1">
                  <span>Profit</span>
                  <div
                    className={cn(
                      "w-2 h-2 shrink-0",
                      sortDirection === "asc" &&
                        sortBy === "profit" &&
                        "rotate-180",
                    )}
                  >
                    {arrow}
                  </div>
                </div>
              </th>
              <th className="w-[10%] px-1.5 py-2 mobile:px-2 touch-device:px-4 last:rounded-tr-lg">
                <span className="hidden touch-device:block">Properties</span>
                <span className="block touch-device:hidden">Props</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {players.map((player, index) => {
              const playerPriceFrom = getPriceType(
                player.price,
                player.sbcPrice,
                player.isSbcPlayer,
                player.isObjectivePlayer,
              );
              const isSelected = selectedPlayers.some(
                (p) => p.eaPlayerId === player.eaPlayerId,
              );
              return (
                <tr
                  key={
                    player.id +
                    player.eaPlayerId +
                    player.price +
                    player.profit +
                    player.sbcStorage +
                    player.untradeable +
                    player.activeSquad +
                    player.duplicate +
                    player.transferList
                  }
                  className={cn(
                    index % 2 === 0 ? "bg-[#23282E]" : "bg-panel-background",
                    isSelecting &&
                      !isSelected &&
                      "opacity-80 hover:opacity-100",
                    isSelected
                      ? "bg-panel-foreground"
                      : "hover:bg-panel-foreground/80",
                    "space-y-1 cursor-pointer group",
                  )}
                  onClick={() => {
                    if (isSelecting) {
                      handleSelectPlayer(player, isSelected);
                    } else {
                      const navLink = generateNavigationUrl({
                        playerRoleId: player.playerRoleId,
                        resourceId: player.resourceId,
                        name: player.displayName,
                        evolutionPath: player.evolutionPath,
                        evoPathId: player.evoPathId,
                        canEvolve: player.canEvolve,
                      });
                      navigate(navLink, {
                        state: {
                          from: "/my-club/players",
                          scrollPosition: window.scrollY,
                        },
                      });
                    }
                  }}
                >
                  <td className="mx-auto text-left flex flex-row gap-x-1 mobile:gap-x-2 items-center justify-start pl-2 my-auto py-1">
                    <div className="relative">
                      <SquarePlayer
                        metalId={player.metalId}
                        versionId={player.versionId}
                        playerUrl={player.playerUrl}
                      />
                      {isSelecting && (
                        <div
                          className={cn(
                            "absolute bottom-1 right-1 flex items-center fill-white justify-center w-4 h-4 mobile:w-5 mobile:h-5 rounded-full shadow-hard bg-primary-dark-blue ",
                            !isSelected &&
                              "brightness-60 group-hover:brightness-100",
                          )}
                        >
                          {checkMark}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <p className="max-w-24 mobile:max-w-none truncate text-dark-secondary">
                        {player.displayName}
                      </p>
                      <div className="flex flex-row gap-x-1 items-center">
                        <img
                          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/clubs/dark/${player.clubId}.png`}
                          alt="flag"
                          className="h-4 mobile:h-6"
                        />
                        <img
                          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/countries/${player.countryId}.png`}
                          alt="flag"
                          className="h-3 mobile:h-4"
                        />
                      </div>
                    </div>
                  </td>
                  <td
                    className={cn(
                      "px-1.5 py-2 mobile:px-2 touch-device:px-4 mx-auto",
                      sortBy === "rating"
                        ? "text-dark-secondary"
                        : "text-dark-tertiary",
                    )}
                  >
                    {player.rating}
                  </td>
                  <td className="px-1.5 py-2 mobile:px-2 touch-device:px-4 mx-auto hidden mobile:table-cell text-dark-tertiary">
                    {player.position}
                  </td>
                  <td className="px-1.5 py-2 mx-auto hidden touch-device:table-cell text-dark-tertiary">
                    {versionAssets[player.versionId]?.name}
                  </td>
                  <td
                    className={cn(
                      "px-1.5 py-2 mobile:px-2 touch-device:px-4 mx-auto",
                      sortBy === "price"
                        ? "text-dark-secondary"
                        : "text-dark-tertiary",
                    )}
                  >
                    {playerPriceFrom === "sbc"
                      ? convertPlayerPrice(player.sbcPrice, isMobile)
                      : player.price
                        ? convertPlayerPrice(player.price, isMobile)
                        : "-"}
                  </td>
                  <td
                    className={cn(
                      "px-1.5 py-2 mobile:px-2 touch-device:px-4 mx-auto",
                      player.profit
                        ? player.profit > 0
                          ? "text-complete"
                          : "text-error"
                        : "text-dark-tertiary",
                    )}
                  >
                    {player.profit
                      ? convertPlayerPrice(player.profit, isMobile)
                      : "-"}
                  </td>
                  <td className="px-1 touch-device:px-4 items-center justify-center">
                    <div className="flex flex-row gap-0-5 mobile:gap-1 items-center justify-center mobile:h-[56px]">
                      {player.activeSquad && (
                        <IconWithTooltip
                          icon={
                            <img
                              className="w-4 h-4 mobile:w-6 mobile:h-6"
                              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/active-squad.png`}
                              alt="active squad"
                            />
                          }
                          tooltip="Active Squad"
                        />
                      )}
                      {player.untradeable && (
                        <IconWithTooltip
                          icon={
                            <img
                              className="w-4 h-4 mobile:w-6 mobile:h-6"
                              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/untradeable.png`}
                              alt="untradeable"
                            />
                          }
                          tooltip="Untradeable"
                        />
                      )}
                      {player.transferList && (
                        <IconWithTooltip
                          icon={
                            <img
                              className="w-4 h-4 mobile:w-6 mobile:h-6"
                              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/transfer-list.png`}
                              alt="transfer list"
                            />
                          }
                          tooltip="Transfer List"
                        />
                      )}
                      {player.duplicate && (
                        <IconWithTooltip
                          icon={
                            <img
                              className="w-4 h-4 mobile:w-6 mobile:h-6"
                              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/duplicate.png`}
                              alt="duplicate"
                            />
                          }
                          tooltip="Duplicate"
                        />
                      )}
                      {player.sbcStorage && (
                        <IconWithTooltip
                          icon={
                            <img
                              className="w-4 h-4 mobile:w-6 mobile:h-6"
                              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/sbc-storage.png`}
                              alt="sbc storage"
                            />
                          }
                          tooltip="SBC Storage"
                        />
                      )}
                      {player.manuallyAdded && (
                        <IconWithTooltip
                          icon={
                            <img
                              className="w-4 h-4 mobile:w-6 mobile:h-6"
                              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/manually-added.png`}
                              alt="manually added"
                            />
                          }
                          tooltip="Manually Added"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {players.length > 0 && (
        <div className="flex justify-end w-full p-4 mb-8">
          <Pagination
            currentPage={page}
            totalAmountOfPages={totalAmountOfPages}
            changePage={setPage}
          />
        </div>
      )}
    </main>
  );
};

const IconWithTooltip = ({
  icon,
  tooltip,
}: {
  icon: React.ReactNode;
  tooltip: string;
}) => {
  return (
    <Tooltip content={tooltip} side="bottom" delayDuration={100}>
      <div className="cursor-default">{icon}</div>
    </Tooltip>
  );
};
